// @flow
import invariant from 'invariant';
import { configValue } from 'utils/config';
import {
  removeKeys,
  isEmpty,
  isNotEmpty,
  gather,
  setEquals,
  values,
  isDefined,
  identity
} from 'utils/functional';
import { sideFacetsForType } from 'utils/search/searchFacets';
import { SEARCH_PAGE_SIZE } from 'utils/constants';
import type { FiltersWithType } from 'utils/search/getSearchUrl';
import getVolopsSearchIndex from 'utils/search/getVolopsSearchIndex';
import getGradSchoolsSearchIndex from 'utils/search/getGradSchoolsSearchIndex';
import getSearchLocationOptions from './getSearchLocationOptions';
import getSearchIndex from './getSearchIndex';

const queryStringFilter = (facet: string, s: string) =>
  isNotEmpty(s) ? `${facet}:'${s}'` : '';

const getBooleanValue = (b: string): string => {
  if (b === 'YES') return 'true';
  if (b === 'NO') return 'false';
  return '';
};

export const isGradProgram = (type: string) => type === 'GRAD_PROGRAM';
export const isVolunteering = (type: string) => type === 'VOLOP';

// Translate from a 'semantic' filter to an algolia-compatible query string
const fromSemantic = ({ name, operator, value }) =>
  `${name}${operator}${value}`;

const facetQueryFiltersInternal = (
  filters: SearchFilters | SearchFiltersWithType<string>,
  facets: Array<SearchFacet>
): Array<string> => {
  const getValue = (
    { type, name, options }: SearchFacet,
    value: Object
  ): string => {
    if (type === 'date') {
      return isDefined(value)
        ? values(value)
            .filter(identity)
            .filter(x => x.value)
            .map(fromSemantic)
            .join(' AND ')
        : '';
    }
    return typeof value === 'string'
      ? type === 'boolean'
        ? queryStringFilter(name, getBooleanValue(value))
        : value === 'ALL'
        ? ''
        : queryStringFilter(name, value)
      : Array.isArray(value) && type === 'array'
      ? isEmpty(value) || setEquals(value, options)
        ? ''
        : `(${value.map(o => queryStringFilter(name, o)).join(' OR ')})`
      : '';
  };

  const type = filters.type || ''; // using '' for null for flow x_x
  const typeFilter =
    type !== 'ALL' && isNotEmpty(type) ? [queryStringFilter('type', type)] : [];

  return facets
    .filter(f => filters[f.id])
    .map(f => [f, filters[f.id]])
    .map(gather(getValue))
    .filter(isNotEmpty)
    .concat(typeFilter);
};

const facetQueryFilters = (
  filters: FiltersWithType,
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>
) => {
  const facets = sideFacetsForType(searchFacets, filters.type);
  invariant(filters.type, 'type required');
  return facetQueryFiltersInternal(filters, facets);
};

const gradSchoolsFacetQueryFilters = (
  filters: FiltersWithType,
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>
): Array<string> => {
  const facets = values(searchFacets);
  const allowedFilters = removeKeys(filters, ['type']);
  return facetQueryFiltersInternal(allowedFilters, facets);
};

// Happens to be the same as the grad school facet query filter: no 'type'
// Can change if the two ever diverge
const volopsFacetQueryFilters = gradSchoolsFacetQueryFilters;

export const postSearchFacetQueryFilters = (
  filters: SearchFilters,
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>,
  locale: string
): Array<string> => {
  const facets = values(searchFacets);
  return facetQueryFiltersInternal(filters, facets).concat([
    'pageType:POST',
    `locale:${locale}`
  ]);
};

export const getStaffSearchQueryOptions = (
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>,
  {
    location,
    filters,
    pageIndex,
    radius
  }: {
    location: ?SearchLocation,
    filters: SearchFiltersWithType<StaffTypeFilter>,
    pageIndex: number,
    radius: string
  }
): QueryOptions => {
  let searchFilters = facetQueryFilters(filters, searchFacets);

  const index = configValue('algolia', 'staffIndexName');
  if (!index) throw Error('search index not retreived');

  const hitsPerPage = SEARCH_PAGE_SIZE;
  const page = pageIndex || 0;
  const attributesToRetrieve = [
    'objectID',
    'type',
    'status',
    'published',
    'created',
    'expires',
    'archived',
    'name',
    'city',
    'state',
    'country',
    'orgID',
    'orgName',
    'groupID',
    'groupName',
    'orgType',
    'logoHandle',
    'logo',
    'groupID',
    'groupUrl',
    'groupName',
    'localizedStarts',
    'localizedEnds',
    'invoiceNumber',
    'method',
    'transactionId',
    'orgs',
    'email'
  ].join();

  let locationOptions = {};

  if (radius && location && location.latitude && location.longitude) {
    const options = getSearchLocationOptions(radius, location);
    searchFilters = searchFilters.concat(options.locationFilters);
    locationOptions = options.locationOptions;
  }

  return {
    index,
    facets: '*',
    hitsPerPage,
    page,
    attributesToRetrieve,
    filters: searchFilters.join(' AND '),
    ...locationOptions
  };
};

const getSearchQueryOptions = (
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>,
  {
    location,
    filters,
    pageIndex,
    sort,
    radius,
    isMapView,
    isFeaturedJobs
  }: {
    location: ?SearchLocation,
    filters?: $ReadOnly<SearchFiltersWithType<ListingType>>,
    pageIndex: number,
    sort: ?string,
    radius: string,
    isMapView?: ?boolean,
    isFeaturedJobs?: boolean
  }
): QueryOptions => {
  const type = isFeaturedJobs || !filters ? 'JOBS' : filters.type;

  let searchFilters = isFeaturedJobs
    ? ['type: "JOB"', 'isFeatured: true']
    : !filters
    ? []
    : isGradProgram(type)
    ? gradSchoolsFacetQueryFilters(filters, searchFacets)
    : isVolunteering(type)
    ? volopsFacetQueryFilters(filters, searchFacets)
    : facetQueryFilters(filters, searchFacets);

  const index =
    !filters || isFeaturedJobs
      ? getSearchIndex(sort)
      : isGradProgram(type)
      ? getGradSchoolsSearchIndex(sort)
      : isVolunteering(type)
      ? getVolopsSearchIndex(filters.actionDateFacet, sort)
      : getSearchIndex(sort);
  if (!index) throw Error('search index not retreived');

  const hitsPerPage = isMapView ? 100 : SEARCH_PAGE_SIZE;
  const attributesToSnippet = ['description:20'];
  const page = isMapView ? 0 : pageIndex || 0;
  const attributesToRetrieve = isGradProgram(type)
    ? [
        'type',
        'objectID',
        'name',
        'schoolName',
        'city',
        'state',
        'country',
        'url',
        'orgID',
        'orgUrl',
        'orgName',
        'orgType',
        'avgFinancialAid',
        'nbOfStudents',
        'isPublicSchool',
        'acceptanceRate',
        'logoHandle',
        'logo',
        'filters',
        'published',
        'degreeProgramsOffered'
      ]
    : isVolunteering(type)
    ? '*'
    : [
        'objectID',
        'type',
        'published',
        'name',
        'city',
        'state',
        'stateStr',
        'country',
        'url',
        'orgID',
        'orgUrl',
        'orgName',
        'orgType',
        'logoHandle',
        'imageHandle',
        'logo',
        'groupID',
        'groupUrl',
        'groupName',
        'isFullTime',
        'listingLocationType',
        'paid',
        'payment',
        'localizedStarts',
        'localizedEnds',
        '_geoloc',
        'hasPublishedListings',
        'actionType',
        'actionSubType',
        'source',
        'timezone',
        'hasAts',
        'isPostedAnonymously',
        'isFeatured'
      ].join();

  let locationOptions = {};

  if (
    (isFeaturedJobs || (filters && filters.hasLocationFacet !== 'NO')) && // Special case for this facet to ignore user location
    radius &&
    location &&
    location.latitude &&
    location.longitude
  ) {
    const options = getSearchLocationOptions(radius, location);
    searchFilters = searchFilters.concat(options.locationFilters);
    locationOptions = options.locationOptions;
  }

  return {
    index,
    facets: '*',
    hitsPerPage,
    page,
    attributesToSnippet,
    attributesToRetrieve,
    filters: searchFilters.join(' AND '),
    ...locationOptions
  };
};

export default getSearchQueryOptions;
